import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { defaultDebounceMs } from '@/constants/variables';
import { useBoolean } from '@/hooks';
import { cn } from '@/lib/utils';
import type { FormOption, FormOptionGroup, FormOptionGroups, FormOptions } from '@/types/general';

import { SpinnerLoader } from '../Loading';
import { Badge } from './badge';
import { Button } from './button';
import { Skeleton } from './skeleton';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './command';
import { Icon } from './icon';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

const comboBoxVariants = cva(
  'peer flex w-full rounded-md text-sm transition-all duration-200 ease-in-out disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default: [
          'border-2 border-border hover:border-action',
          'focus-within:border-action focus-within:ring-0 focus:outline-none',
          'data-[placeholder]:text-action',
        ],
        primary: [
          'border-2 border-border hover:border-primary/50',
          'focus-within:border-primary focus-within:ring-0 focus:outline-none',
          'data-[placeholder]:text-muted-foreground',
        ],
        secondary: [
          'border-2 border-border hover:border-secondary/50',
          'focus-within:border-secondary focus-within:ring-0 focus:outline-none',
          'data-[placeholder]:text-muted-foreground',
        ],
        info: [
          'border-2 border-border hover:border-info/50',
          'focus-within:border-info focus-within:ring-0 focus:outline-none',
          'data-[placeholder]:text-muted-foreground',
        ],
        success: [
          'border-2 border-border hover:border-success/50',
          'focus-within:border-success focus-within:ring-0 focus:outline-none',
          'data-[placeholder]:text-muted-foreground',
        ],
        warning: [
          'border-2 border-border hover:border-warning/50',
          'focus-within:border-warning focus-within:ring-0 focus:outline-none',
          'data-[placeholder]:text-muted-foreground',
        ],
        error: [
          'border-2 border-error hover:border-error',
          'focus-within:border-error focus-within:ring-0 focus:outline-none',
          'data-[placeholder]:text-error/60',
        ],
      },
      inputSize: {
        default: 'min-h-[56px]',
        sm: 'min-h-[44px] text-xs',
        lg: 'min-h-[64px] text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      inputSize: 'default',
    },
  },
);

// Export the variants for use in other components
export { comboBoxVariants };

const labelVariants = cva(
  'pointer-events-none absolute left-3 origin-[0] bg-white px-1 text-sm text-muted-foreground transition-all duration-200 ease-in-out',
  {
    variants: {
      variant: {
        default: [
          'peer-focus-within:translate-y-[-26px] peer-focus-within:scale-75 peer-focus-within:text-action-hover',
          'peer-data-[state=open]:translate-y-[-26px] peer-data-[state=open]:scale-75 peer-data-[state=open]:text-action-hover',
          'translate-y-0 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100',
        ],
        primary: [
          'peer-focus-within:translate-y-[-26px] peer-focus-within:scale-75 peer-focus-within:text-primary',
          'peer-data-[state=open]:translate-y-[-26px] peer-data-[state=open]:scale-75 peer-data-[state=open]:text-primary',
          'translate-y-0 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100',
        ],
        secondary: [
          'peer-focus-within:translate-y-[-26px] peer-focus-within:scale-75 peer-focus-within:text-secondary',
          'peer-data-[state=open]:translate-y-[-26px] peer-data-[state=open]:scale-75 peer-data-[state=open]:text-secondary',
          'translate-y-0 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100',
        ],
        info: [
          'peer-focus-within:translate-y-[-26px] peer-focus-within:scale-75 peer-focus-within:text-info',
          'peer-data-[state=open]:translate-y-[-26px] peer-data-[state=open]:scale-75 peer-data-[state=open]:text-info',
          'translate-y-0 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100',
        ],
        success: [
          'peer-focus-within:translate-y-[-26px] peer-focus-within:scale-75 peer-focus-within:text-success',
          'peer-data-[state=open]:translate-y-[-26px] peer-data-[state=open]:scale-75 peer-data-[state=open]:text-success',
          'translate-y-0 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100',
        ],
        warning: [
          'peer-focus-within:translate-y-[-26px] peer-focus-within:scale-75 peer-focus-within:text-warning',
          'peer-data-[state=open]:translate-y-[-26px] peer-data-[state=open]:scale-75 peer-data-[state=open]:text-warning',
          'translate-y-0 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100',
        ],
        error: [
          'peer-focus-within:translate-y-[-26px] peer-focus-within:scale-75 peer-focus-within:text-error',
          'peer-data-[state=open]:translate-y-[-26px] peer-data-[state=open]:scale-75 peer-data-[state=open]:text-error',
          'translate-y-0 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100',
        ],
      },
      inputSize: {
        default: 'top-[16px]',
        sm: 'top-[12px]',
        lg: 'top-[20px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      inputSize: 'default',
    },
  },
);

export type ComboBoxProps = {
  name?: string;
  id?: string;
  title?: string;
  description?: string;
  options: FormOptions | FormOptionGroups;
  loading?: boolean;
  multiple?: boolean;
  onSearchChange?: (searchTerm: string) => Promise<void> | void;
  onAddNew?: (value: string) => void;
  validateNewValue?: (value: string) => boolean;
  searchDebounceMs?: number;
  addNewLabel?: string;
  addNewPlaceholder?: string;
  alwaysShowAddNew?: boolean;
  placeholder?: string;
  variant?: VariantProps<typeof comboBoxVariants>['variant'];
  inputSize?: VariantProps<typeof comboBoxVariants>['inputSize'];
  disabled?: boolean;
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  renderOption?: (option: FormOption, isSelected: boolean) => React.ReactNode;
  renderSelectedValue?: (option: FormOption) => React.ReactNode;
  error?: string;
  startIcon?: string;
  endIcon?: string;
  onEndIconClick?: () => void;
  minSelections?: number;
  lockedValues?: string[];
  lockedValueTooltip?: string;
  unknownOptionLabel?: string;
  loadMoreRef?: (node?: Element | null) => void;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  /** When set, disables the trigger and shows this label with a spinner inside the trigger (in-place loading state). */
  forceLoadingLabel?: string;
};

function ComboBox({
  name,
  id,
  title,
  description,
  placeholder,
  loading = false,
  options,
  multiple = false,
  onSearchChange,
  onAddNew,
  validateNewValue,
  addNewLabel,
  variant = 'default',
  inputSize = 'default',
  searchDebounceMs = defaultDebounceMs,
  disabled = false,
  value,
  onChange,
  renderOption,
  renderSelectedValue,
  error,
  startIcon,
  endIcon,
  onEndIconClick,
  minSelections = 0,
  lockedValues = [],
  lockedValueTooltip,
  unknownOptionLabel,
  loadMoreRef,
  isFetchingNextPage = false,
  hasNextPage = false,
  alwaysShowAddNew = false,
  forceLoadingLabel,
}: ComboBoxProps) {
  const translate = useTranslations();

  const isSearching = useBoolean();
  const isPopoverOpen = useBoolean();
  const [searchValue, setSearchValue] = useState('');

  // Ensure value is an array when multiple is true
  const normalizedValue = useMemo(() => {
    if (multiple === true) {
      return Array.isArray(value) ? value : value ? [value] : [];
    }
    return value;
  }, [value, multiple]);

  const lockedValuesSet = useMemo(() => new Set(lockedValues), [lockedValues]);

  const selectedOptionsMap = useMemo(() => {
    if (multiple === false || Array.isArray(normalizedValue) === false) {
      return new Map();
    }
    return new Map(normalizedValue.map(v => [v, true]));
  }, [normalizedValue, multiple]);

  const { optionsMap, isGrouped, groupedOptions, flatOptions } = useMemo(() => {
    const isOptionGroup = (opt: FormOption | FormOptionGroup): opt is FormOptionGroup => {
      return 'label' in opt && 'options' in opt;
    };

    /**
     * Deduplicates options by value, keeping the first occurrence
     * This handles cases where infinite loading might return duplicate items
     */
    const deduplicateOptions = (opts: FormOptions): FormOptions => {
      const seenValues = new Set<string>();
      return opts.filter((opt) => {
        if (seenValues.has(opt.value) === true) {
          return false;
        }
        seenValues.add(opt.value);
        return true;
      });
    };

    if (Array.isArray(options) && options.length > 0 && options[0] && isOptionGroup(options[0])) {
      // Grouped options - deduplicate within each group
      const groups = options as FormOptionGroups;
      const allOptionsMap = new Map<string, string>();
      const seenValuesAcrossGroups = new Set<string>();

      const deduplicatedGroups = groups.map((group) => {
        const uniqueOptions = group.options.filter((opt) => {
          if (seenValuesAcrossGroups.has(opt.value) === true) {
            return false;
          }
          seenValuesAcrossGroups.add(opt.value);
          allOptionsMap.set(opt.value, opt.label);
          return true;
        });
        return { ...group, options: uniqueOptions };
      }).filter(group => group.options.length > 0);

      return {
        optionsMap: allOptionsMap,
        isGrouped: true,
        groupedOptions: deduplicatedGroups,
        flatOptions: deduplicatedGroups.flatMap(g => g.options),
      };
    }

    // Flat options - deduplicate by value
    const flatOpts = deduplicateOptions(options as FormOptions);
    return {
      optionsMap: new Map(flatOpts.map(opt => [opt.value, opt.label])),
      isGrouped: false,
      groupedOptions: [] as FormOptionGroups,
      flatOptions: flatOpts,
    };
  }, [options]);

  const hasValue = useMemo(() => {
    if (multiple === true) {
      return Array.isArray(normalizedValue) && normalizedValue.length > 0;
    }
    return normalizedValue !== null && normalizedValue !== undefined && normalizedValue !== '';
  }, [normalizedValue, multiple]);

  const SelectedBadges = useMemo(() => {
    if (
      multiple === false ||
      Array.isArray(normalizedValue) === false ||
      normalizedValue.length === 0
    ) {
      return null;
    }

    return (
      <div className="flex flex-wrap gap-1.5">
        {normalizedValue.map((val: string) => {
          const selectedOption = flatOptions.find(opt => opt.value === val);
          const isLocked = lockedValuesSet.has(val);

          const badge = (
            <Badge
              variant="secondary"
              key={val}
              className="flex h-[22px] shrink-0 items-center gap-1 bg-action/10 text-xs text-action hover:bg-action/20"
            >
              {renderSelectedValue && selectedOption ? (
                <div className="flex items-center gap-1 max-w-[140px] truncate">
                  {renderSelectedValue(selectedOption)}
                </div>
              ) : (
                <span className="max-w-[120px] truncate">
                  {optionsMap.get(val) || unknownOptionLabel || translate('abTest.unknownOption')}
                </span>
              )}
              {isLocked === false && (
                <button
                  type="button"
                  className="p-0 text-action hover:text-error"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (Array.isArray(normalizedValue) === false) {
                      return;
                    }
                    const newValues = normalizedValue.filter(
                      (item: string) => item !== val
                    );
                    if (onChange) {
                      onChange(newValues);
                    }
                  }}
                >
                  <Icon
                    icon="material-symbols:close"
                    className="cursor-pointer p-0 hover:text-error"
                    size={15}
                  />
                </button>
              )}
            </Badge>
          );

          if (isLocked === true && lockedValueTooltip !== undefined) {
            return (
              <Tooltip key={val}>
                <TooltipTrigger asChild>
                  <span>{badge}</span>
                </TooltipTrigger>
                <TooltipContent>
                  {lockedValueTooltip}
                </TooltipContent>
              </Tooltip>
            );
          }

          return badge;
        })}
      </div>
    );
  }, [normalizedValue, optionsMap, multiple, onChange, translate, renderSelectedValue, flatOptions, lockedValuesSet, lockedValueTooltip]);

  // Only apply manual filtering when we're doing API-based search (onSearchChange is provided)
  // When onSearchChange is undefined, CMDK handles filtering via shouldFilter={true}
  const filteredOptions = useMemo(() => {
    // If onSearchChange is provided, the API handles filtering - don't filter locally
    if (onSearchChange !== undefined) {
      return isGrouped ? groupedOptions : flatOptions;
    }

    if (searchValue === '') {
      return isGrouped ? groupedOptions : flatOptions;
    }
    const searchLower = searchValue.toLowerCase();

    if (isGrouped) {
      return groupedOptions
        .map(group => ({
          ...group,
          options: group.options.filter((option) => {
            // Check label first (primary match)
            const labelMatch = option.label.toLowerCase().includes(searchLower);
            // Only check value if label doesn't match
            const valueMatch = labelMatch === false && option.value.toLowerCase().includes(searchLower);

            return labelMatch || valueMatch;
          }),
        }))
        .filter(group => group.options.length > 0);
    }

    return flatOptions.filter((option) => {
      // Check label first (primary match)
      const labelMatch = option.label.toLowerCase().includes(searchLower);
      // Only check value if label doesn't match
      const valueMatch = labelMatch === false && option.value.toLowerCase().includes(searchLower);

      return labelMatch || valueMatch;
    });
  }, [searchValue, isGrouped, groupedOptions, flatOptions, onSearchChange]);

  const finalAddNewLabel: string
    = addNewLabel || translate('filters.addNewOption', { value: searchValue });

  // Debounced search handler
  const debouncedSearch = useDebouncedCallback(async (value: string) => {
    if (onSearchChange) {
      await onSearchChange(value);
    }
    isSearching.onFalse();
  }, searchDebounceMs);

  const handleSearchInput = useCallback(
    (value: string) => {
      setSearchValue(value);
      if (onSearchChange) {
        if (isSearching.value === false) {
          isSearching.onTrue();
        }
        void debouncedSearch(value);
      }
    },
    [debouncedSearch, onSearchChange, isSearching],
  );

  const handleSelect = (selectedValue: string) => {
    if (multiple === true) {
      const currentValues = Array.isArray(normalizedValue) ? normalizedValue : [];
      const isDeselecting = currentValues.includes(selectedValue);
      // Prevent deselecting locked values
      if (isDeselecting === true && lockedValuesSet.has(selectedValue)) {
        return;
      }
      const newValues = isDeselecting
        ? currentValues.filter((item: string) => item !== selectedValue)
        : [...currentValues, selectedValue];
      if (onChange) {
        onChange(newValues);
      }
    } else {
      if (onChange) {
        onChange(selectedValue);
      }
      isPopoverOpen.onFalse();
    }
  };

  const handleRemove = (valueToRemove: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (Array.isArray(normalizedValue) === false) {
      return;
    }
    // Prevent removal of locked values
    if (lockedValuesSet.has(valueToRemove)) {
      return;
    }
    // Prevent removal if it would go below minimum selections
    if (normalizedValue.length <= minSelections) {
      return;
    }
    const newValues = normalizedValue.filter((item: string) => item !== valueToRemove);
    if (onChange) {
      onChange(newValues);
    }
  };

  const handleClearAll = () => {
    if (onChange !== undefined) {
      if (multiple === true && Array.isArray(normalizedValue)) {
        const keptValues = normalizedValue.filter((val: string) => lockedValuesSet.has(val));
        onChange(keptValues);
      } else {
        onChange(multiple ? [] : '');
      }
    }
  };

  const handleSelectAll = () => {
    if (multiple && onChange) {
      onChange(flatOptions.map(option => option.value));
    }
  };

  // Update the condition to show loading skeleton
  const isShowingSkeleton = isSearching.value || loading;

  const handleAddNew = (newValue: string) => {
    if (onAddNew) {
      onAddNew(newValue);
      // Reset search state completely
      setSearchValue('');
      // Close the popover after a slight delay to ensure the new value is processed
      setTimeout(() => {
        isPopoverOpen.onFalse();
      }, 100);
    }
  };

  const isAddNewValid = useMemo(() => {
    if (validateNewValue === undefined) {
      return true;
    }
    return validateNewValue(searchValue);
  }, [searchValue, validateNewValue]);

  let buttonContent: React.ReactNode = null;
  if (multiple === true) {
    if (Array.isArray(normalizedValue) && normalizedValue.length > 0) {
      buttonContent = (
        <div className="flex-1">
          <div className="flex flex-wrap gap-1.5">
            {normalizedValue.map((val: string) => {
              const selectedOption = flatOptions.find(opt => opt.value === val);
              return (
                <Badge
                  variant="secondary"
                  key={val}
                  className="flex h-[22px] shrink-0 items-center gap-1 bg-action/10 text-xs text-action hover:bg-action/20"
                >
                  {renderSelectedValue && selectedOption ? (
                    <div className="flex items-center gap-1 max-w-[140px] truncate">
                      {renderSelectedValue(selectedOption)}
                    </div>
                  ) : (
                    <span className="max-w-[120px] truncate">
                      {optionsMap.get(val) || unknownOptionLabel || translate('abTest.unknownOption')}
                    </span>
                  )}
                <Button
                  variant="ghost"
                  size="icon-xs"
                  className="text-action hover:text-error"
                  onClick={(event) => {
                    handleRemove(val, event);
                  }}
                  disabled={Array.isArray(normalizedValue) && normalizedValue.length <= minSelections}
                >
                  <Icon
                    icon="material-symbols:close"
                    className="cursor-pointer hover:text-error"
                  />
                </Button>
              </Badge>
              );
            })}
          </div>
        </div>
      );
    }
  } else {
    // Find the full option object for the selected value
    const selectedOption = flatOptions.find(opt => opt.value === normalizedValue);
    
    if (renderSelectedValue && selectedOption) {
      buttonContent = renderSelectedValue(selectedOption);
    } else {
      const selectedOptionLabel = loading === false
        ? optionsMap.get(normalizedValue as string)
        : null;
      buttonContent = <span className="truncate">{selectedOptionLabel}</span>;
    }
  }

  /**
   * Renders an option item with consistent formatting
   * Uses custom renderOption function if provided, otherwise renders the default view
   */
  const renderOptionItem = useCallback(
    (option: FormOption, currentFieldValue: string | string[] | undefined): React.ReactNode => {
      // Determine if this option is selected
      let isSelected = false;

      if (multiple === true && Array.isArray(currentFieldValue)) {
        isSelected = selectedOptionsMap.has(option.value);
      } else if (currentFieldValue !== undefined) {
        isSelected = option.value === currentFieldValue;
      }

      // Use custom render function if provided
      if (renderOption !== undefined) {
        return renderOption(option, isSelected);
      }

      return (
        <>
          <Icon
            icon="material-symbols:check"
            className={cn('mr-2', isSelected === true ? 'opacity-100' : 'opacity-0')}
          />
          <span className="truncate">{option.label}</span>
        </>
      );
    },
    [multiple, renderOption, selectedOptionsMap],
  );

  return (
    <div className="w-full">
      <Popover
        modal
        open={isPopoverOpen.value}
        onOpenChange={(open) => {
          if (disabled === true) {
            return;
          }
          // Reset search term when closing the dropdown
          if (open === false) {
            setSearchValue('');
          }
          isPopoverOpen.setValue(open);
        }}
      >
        <div className="relative w-full">
          <PopoverTrigger asChild disabled={disabled === true || forceLoadingLabel !== undefined}>
            <div
              id={id}
              role="button"
              tabIndex={disabled === true || forceLoadingLabel !== undefined ? -1 : 0}
              onKeyDown={(e) => {
                if (disabled === true || forceLoadingLabel !== undefined) {
                  return;
                }
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  isPopoverOpen.setValue(!isPopoverOpen.value);
                }
              }}
              className={cn(
                comboBoxVariants({ variant, inputSize }),
                'bg-white text-foreground',
                disabled === false && forceLoadingLabel === undefined ? 'hover:cursor-pointer' : 'cursor-not-allowed',
                'flex items-center justify-between gap-2 px-3 py-2',
                'relative w-full',
                multiple && normalizedValue && Array.isArray(normalizedValue) && normalizedValue.length > 0 && 'items-center',
                (disabled === true || forceLoadingLabel !== undefined) && 'opacity-50',
                error && 'border-error hover:border-error',
              )}
              onClick={(e) => {
                if (disabled === true || forceLoadingLabel !== undefined) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              aria-disabled={disabled === true || forceLoadingLabel !== undefined}
            >
              {/* Start icon */}
              {startIcon && (
                <Icon
                  icon={startIcon}
                  className="text-xl text-muted-foreground flex-shrink-0 mr-2"
                />
              )}

              {/* In-place loading state — replaces normal trigger content */}
              {forceLoadingLabel !== undefined && (
                <span className="flex min-w-0 flex-1 items-center gap-2 text-muted-foreground select-none">
                  <SpinnerLoader size="sm" />
                  <span>{forceLoadingLabel}</span>
                </span>
              )}

              {forceLoadingLabel === undefined && (
                <span
                  className={cn(
                    'flex min-w-0 flex-1',
                    hasValue === false && 'text-muted-foreground',
                    hasValue === false && 'select-none',
                    multiple && normalizedValue && Array.isArray(normalizedValue) && normalizedValue.length > 0
                      ? 'items-center'
                      : 'items-center',
                  )}
                >
                  {multiple === true
                    ? (
                      <div className="max-h-[56px] w-full overflow-y-auto">
                        {SelectedBadges}
                      </div>
                    )
                    : (
                      buttonContent || placeholder || 'Select...'
                    )}
                </span>
              )}
              <div className="flex shrink-0 items-center justify-center gap-1">
                {endIcon !== undefined && onEndIconClick !== undefined && (
                  <Button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEndIconClick();
                    }}
                    variant="ghost"
                    size="icon-xs"
                    className="text-muted-foreground hover:text-action"
                  >
                    <Icon
                      icon={endIcon}
                      size="xs"
                    />
                  </Button>
                )}
                {multiple === true && (
                  <>
                    {Array.isArray(normalizedValue) && normalizedValue.length > minSelections && normalizedValue.some((val: string) => lockedValuesSet.has(val) === false) && (
                      <Button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearAll();
                        }}
                        variant="ghost"
                        size="icon-xs"
                        className="text-action hover:text-error"
                      >
                        <Icon
                          icon="material-symbols:close"
                          size="xs"
                        />
                      </Button>
                    )}
                    {Array.isArray(normalizedValue) && normalizedValue.length < flatOptions.length && (
                      <Button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectAll();
                        }}
                        variant="ghost"
                        size="icon-xs"
                        className="text-action hover:text-success"
                      >
                        <Icon
                          icon="material-symbols:select-all"
                          size="xs"
                        />
                      </Button>
                    )}
                  </>
                )}
                <Icon
                  icon="material-symbols:arrow-drop-down"
                  className="shrink-0 opacity-50"
                />
              </div>
            </div>
          </PopoverTrigger>
          <label
            className={cn(
              labelVariants({ variant, inputSize }),
              (hasValue || isPopoverOpen.value || forceLoadingLabel !== undefined)
              && '-translate-y-[26px] scale-75 bg-white',
              hasValue && 'z-10',
              error && 'text-error peer-focus-within:text-error peer-data-[state=open]:text-error',
            )}
          >
            {title}
          </label>
        </div>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] border-2 border-border p-0"
        >
          <Command shouldFilter={onSearchChange === undefined}>
            <CommandInput
              placeholder={translate('filters.searchPlaceholder', {
                searchPlaceholder: placeholder || '',
              })}
              onValueChange={handleSearchInput}
              value={searchValue}
              className="border-0 focus:ring-0"
            />
            <CommandList className="max-h-[300px] overflow-y-auto">
              {isShowingSkeleton === true
                ? (
                  <div className="my-2 flex h-full items-center justify-center">
                    <SpinnerLoader size="sm" />
                  </div>
                )
                : (
                  <>
                    {filteredOptions.length === 0
                      ? (
                        <CommandEmpty className="p-0">
                          {onAddNew && (searchValue.length > 0 || alwaysShowAddNew === true)
                            ? (
                              <Button
                                variant="ghost"
                                className="w-full justify-start rounded-none p-2 text-sm hover:bg-primary/5"
                                onClick={() => {
                                  handleAddNew(searchValue);
                                }}
                                startIcon="material-symbols:add"
                                disabled={loading || isAddNewValid === false}
                              >
                                {finalAddNewLabel}
                              </Button>
                            )
                            : (
                              <div className="p-2 text-sm text-muted-foreground">
                                {translate('filters.noOptionTitleFound', {
                                  optionTitle: title || '',
                                })}
                              </div>
                            )}
                        </CommandEmpty>
                      )
                      : (
                        <>
                          {isGrouped === true
                            ? (
                              <>
                                {(filteredOptions as FormOptionGroups).map((group, index) => (
                                  <div key={group.label}>
                                    {index > 0 && <CommandSeparator className="my-1.5" />}
                                    <div className={cn(
                                      group.disabled === true && 'opacity-50 cursor-not-allowed',
                                    )}
                                    >
                                      <CommandGroup
                                        heading={group.label}
                                        className="py-1.5"
                                      >
                                        {group.description && (
                                          <div className="text-xs text-muted-foreground leading-normal mx-2.5 -mt-1 mb-3">
                                            {group.description}
                                          </div>
                                        )}
                                        {group.options.map(option => (
                                          <CommandItem
                                            data-testid={name}
                                            value={`${option.label}_${option.value}`}
                                            key={option.value}
                                            onSelect={() => {
                                              if (option.disabled === true || group.disabled === true) {
                                                return;
                                              }
                                              handleSelect(option.value);
                                            }}
                                            className={cn(
                                              'hover:bg-primary/5 focus:bg-primary/5 mx-1.5 my-0.5 rounded',
                                              (option.disabled === true || group.disabled === true) && 'opacity-50 cursor-not-allowed',
                                            )}
                                            disabled={option.disabled === true || group.disabled === true}
                                          >
                                            {renderOptionItem(option, normalizedValue)}
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    </div>
                                  </div>
                                ))}
                              </>
                            )
                            : (
                              <CommandGroup>
                                {(filteredOptions as FormOptions).map(option => (
                                  <CommandItem
                                    data-testid={name}
                                    value={`${option.label}_${option.value}`}
                                    key={option.value}
                                    onSelect={() => {
                                      if (option.disabled === true) {
                                        return;
                                      }
                                      handleSelect(option.value);
                                    }}
                                    className={cn(
                                      'hover:bg-primary/5 focus:bg-primary/5',
                                      option.disabled === true && 'opacity-50 cursor-not-allowed',
                                    )}
                                    disabled={option.disabled === true}
                                  >
                                    {renderOptionItem(option, normalizedValue)}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            )}
                          {/* Infinite scroll load more trigger and skeleton */}
                          {loadMoreRef !== undefined && (hasNextPage === true || isFetchingNextPage === true) && (
                            <div
                              ref={loadMoreRef}
                              className="w-full px-2 py-2"
                            >
                              {isFetchingNextPage === true && (
                                <div className="flex w-full flex-col gap-1.5">
                                  <Skeleton className="h-9 w-full rounded-sm" />
                                  <Skeleton className="h-9 w-full rounded-sm" />
                                  <Skeleton className="h-9 w-full rounded-sm" />
                                </div>
                              )}
                            </div>
                          )}
                          {onAddNew && (searchValue.length > 0 || alwaysShowAddNew === true) && (
                            <CommandGroup>
                              <Button
                                variant="ghost"
                                className="w-full justify-start rounded-none p-2 text-sm hover:bg-primary/5"
                                onClick={() => {
                                  handleAddNew(searchValue);
                                }}
                                startIcon="material-symbols:add"
                                disabled={loading || isAddNewValid === false}
                              >
                                {finalAddNewLabel}
                              </Button>
                            </CommandGroup>
                          )}
                        </>
                      )}
                  </>
                )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
      {error && <p className="mt-2 text-sm font-medium text-error">{error}</p>}
    </div>
  );
}

export default ComboBox; 